import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

const PLANS = [
  {
    key: "starter",
    name: "Compliche Starter",
    subtitle: "Built for the adviser who works alone but cannot afford to get compliance wrong.",
    priceMonthly: 49,
    priceAnnual: 490,
    features: [
      "20 active clients",
      "30 compliance scans / month (FCA · ASA · ICO)",
      "2 onboarding flows",
      "1 team member (account owner)",
      "Branded client portal",
      "5 AML screenings / month",
      "E-signature with IP + timestamp",
      "Audit trail — 7-year retention, view-only",
      "Email support (48hr)",
      "5GB storage",
    ],
    cta: "Start 14-day trial",
    ctaTo: "/signup?plan=starter",
    highlighted: false,
  },
  {
    key: "practitioner",
    name: "Compliche Practitioner",
    subtitle: "Designed around how regulated professionals actually work.",
    priceMonthly: 99,
    priceAnnual: 990,
    features: [
      "100 active clients",
      "Unlimited compliance scans",
      "5 onboarding flows + custom templates",
      "3 team members",
      "30 AML screenings / month",
      "5 identity verifications / month (Didit)",
      "Client onboarding pack PDF",
      "FCA Handbook change alerts",
      "Full audit trail export (PDF + CSV)",
      "Email support (24hr) · 10GB storage",
    ],
    cta: "Start 14-day trial",
    ctaTo: "/signup?plan=practitioner",
    highlighted: true,
    badge: "Most popular",
  },
  {
    key: "professional",
    name: "Compliche Professional",
    subtitle: "The complete compliance infrastructure for serious regulated firms.",
    priceMonthly: 199,
    priceAnnual: 1908,
    features: [
      "Unlimited clients, flows & scans",
      "10 team members",
      "100 AML & 50 identity verifications / month",
      "Overage: £1/IDV · £0.05/AML over allowance",
      "Compliche Proprietary API (1,000 calls / month)",
      "Consumer Duty evidence pack",
      "Supervision & sign-off workflow",
      "Complaints log + 8-week countdown",
      "Vulnerable customer flagging",
      "Annual review scheduling",
      "Priority support (4hr) · 50GB storage",
    ],
    cta: "Start 14-day trial",
    ctaTo: "/signup?plan=professional",
    highlighted: false,
  },
  {
    key: "enterprise",
    name: "Compliche Enterprise",
    subtitle: "Built for firms that manage compliance at scale.",
    priceMonthly: 499,
    priceAnnual: 4788,
    features: [
      "Everything in Professional",
      "Unlimited team, IDV & AML",
      "Unlimited API + webhooks",
      "Multi-firm management",
      "White-label client portal + custom domain",
      "SSO / SAML",
      "Quarterly Compliance Health Report",
      "Dedicated account manager + phone support",
      "99.9% uptime SLA",
      "Unlimited storage · 3-month minimum",
    ],
    cta: "Book a demo",
    ctaTo: "mailto:hello@compliche.com",
    highlighted: false,
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="t-label text-indigo2 mb-3">Pricing</div>
          <h2 className="t-display mb-4">Transparent pricing. No surprises.</h2>
          <p className="t-body-lg text-slate2">
            All plans include a 14-day free trial. Card required at signup — no charge until day 15.
          </p>

          <div className="mt-8 inline-flex items-center bg-surface rounded-full p-1 border border-border2" data-testid="pricing-toggle">
            <button onClick={() => setAnnual(false)} className={`px-5 py-2 rounded-full t-body-sm font-medium transition-all ${!annual ? "bg-white shadow-card text-ink" : "text-slate2"}`} data-testid="pricing-monthly">
              Monthly
            </button>
            <button onClick={() => setAnnual(true)} className={`px-5 py-2 rounded-full t-body-sm font-medium transition-all ${annual ? "bg-white shadow-card text-ink" : "text-slate2"}`} data-testid="pricing-annual">
              Annual <span className="badge2 badge-verified ml-1" style={{ padding: "1px 6px" }}>Save up to 20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {PLANS.map((p) => {
            const monthlyEquiv = annual ? Math.round(p.priceAnnual / 12) : p.priceMonthly;
            const annualSaving = p.priceMonthly * 12 - p.priceAnnual;
            return (
              <div
                key={p.key}
                className={`card2 p-6 flex flex-col relative ${p.highlighted ? "lg:-translate-y-3 lg:shadow-card-hover" : ""}`}
                style={p.highlighted ? { borderColor: "#4F46E5", borderWidth: 2 } : {}}
                data-testid={`pricing-plan-${p.key}`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo2 text-white text-xs font-semibold rounded-full uppercase tracking-wider flex items-center gap-1">
                    <Sparkles size={12} /> {p.badge}
                  </div>
                )}
                <div>
                  <h3 className="t-h3">{p.name.replace("Compliche ", "")}</h3>
                  <p className="t-body-sm text-slate2 mt-1 mb-5 min-h-[44px]">{p.subtitle}</p>
                  <div className="mb-5">
                    <span className="t-mono" style={{ fontSize: 38, fontWeight: 700, letterSpacing: "-0.02em" }}>
                      £{monthlyEquiv}
                    </span>
                    <span className="t-body text-slate2"> /mo</span>
                    {annual && (
                      <div className="t-caption mt-1">
                        £{p.priceAnnual}/yr · save £{annualSaving}
                      </div>
                    )}
                    {!annual && <div className="t-caption mt-1">Billed monthly</div>}
                  </div>
                </div>

                <ul className="space-y-2 flex-1 mb-6">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 t-body-sm">
                      <Check size={16} className="text-emerald2 mt-0.5 flex-shrink-0" strokeWidth={2.6} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {p.ctaTo.startsWith("mailto") ? (
                  <a href={p.ctaTo} className={`btn ${p.highlighted ? "btn-primary" : "btn-secondary"} btn-block`}>{p.cta}</a>
                ) : (
                  <Link to={p.ctaTo} className={`btn ${p.highlighted ? "btn-primary" : "btn-secondary"} btn-block`} data-testid={`pricing-cta-${p.key}`}>{p.cta}</Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
